<section class="privacy-policy">

  <header>
    <h1>Privacy Policy</h1>

    <address>
      <strong>iFlyFinance, Inc.</strong><br />
      1881 California Ave, Suite 101<br />
      Corona, CA 92881
    </address>

    <p>
      <strong>Effective Date:</strong> January 6, 2026
    </p>
  </header>

  <p>
    This Privacy Policy explains in detail how iFlyFinance Inc.
    (<strong>"iFlyFinance," "Company," "we," "us," or "our"</strong>)
    collects, uses, discloses, safeguards, and retains personal information
    obtained through our website (<a href="https://www.iflyfinance.com" target="_blank" rel="noopener noreferrer">www.iFlyFinance.com</a>),
    premium finance applications, premium finance agreements, electronic
    communications, customer portals, and related services
    (collectively, the <strong>"Services"</strong>).
  </p>

  <p>
    This Privacy Policy applies to individuals and businesses located in the
    United States, with an initial operational focus on the State of California.
    It is intended to comply with applicable federal and state laws governing
    financial institutions, including the Gramm-Leach-Bliley Act (GLBA) and
    applicable California privacy laws.
  </p>

  <h2>1. Our Privacy Principles</h2>
  <ul>
    <li>We collect personal information only for legitimate business and regulatory purposes</li>
    <li>We limit access to personal information to authorized individuals and service providers</li>
    <li>We maintain safeguards appropriate to the sensitivity of the information</li>
    <li>We do not sell personal information</li>
    <li>We comply with all applicable privacy, lending, and financial services laws</li>
  </ul>

  <h2>2. Information We Collect</h2>

  <h3>2.1 Personal Information Provided by You</h3>
  <ul>
    <li>Full legal name</li>
    <li>Residential or business address</li>
    <li>Email address and telephone number</li>
    <li>Date of birth (where required by law)</li>
    <li>Business name, ownership information, and authorized signatories</li>
    <li>Insurance policy details (policy numbers, premiums, effective dates, carriers)</li>
    <li>Payment information (bank account numbers, ACH authorizations, debit or credit card information)</li>
    <li>Electronic signatures and authorizations</li>
    <li>Communications with iFlyFinance</li>
  </ul>

  <h3>2.2 Information Collected Automatically</h3>
  <ul>
    <li>IP address</li>
    <li>Browser type, operating system, and device identifiers</li>
    <li>Date and time of access</li>
    <li>Pages viewed and actions taken</li>
    <li>Referring URLs</li>
  </ul>

  <h3>2.3 Information Obtained from Third Parties</h3>
  <ul>
    <li>Insurance agents and brokers</li>
    <li>Insurance carriers and managing general agents</li>
    <li>Payment processors and financial institutions</li>
    <li>Identity verification and fraud detection providers</li>
    <li>Regulatory agencies or public records</li>
  </ul>

  <h2>3. How We Use Personal Information</h2>
  <ul>
    <li>Evaluating eligibility for premium finance products</li>
    <li>Administering premium finance agreements</li>
    <li>Funding insurance premiums and managing trust accounts</li>
    <li>Servicing and collecting premium finance loans</li>
    <li>Processing payments, refunds, cancellations, and reinstatements</li>
    <li>Customer communications and disclosures</li>
    <li>Fraud detection and prevention</li>
    <li>Legal, regulatory, and accounting compliance</li>
    <li>Improving systems and services</li>
  </ul>

  <h2>4. Legal Bases for Processing</h2>
  <ul>
    <li>Performance of contractual obligations</li>
    <li>Compliance with legal and regulatory requirements</li>
    <li>Legitimate business interests</li>
    <li>Protection of rights, property, and safety</li>
  </ul>

  <h2>5. Information Sharing and Disclosure</h2>

  <h3>5.1 Service Providers</h3>
  <p>
    We may share personal information with third-party service providers
    performing services on our behalf, all of whom are contractually required
    to protect personal information.
  </p>

  <h3>5.2 Insurance Industry Participants</h3>
  <p>
    Information may be shared with agents, brokers, carriers, and managing
    general agents for premium funding, policy administration, and refunds.
  </p>

  <h3>5.3 Legal and Regulatory Disclosures</h3>
  <p>
    We may disclose information as required by law, regulation, or legal process.
  </p>

  <h3>5.4 Business Transfers</h3>
  <p>
    Personal information may be transferred as part of a merger, acquisition,
    or sale of assets, subject to confidentiality obligations.
  </p>

  <h2>6. Information Security Safeguards</h2>
  <ul>
    <li>Access controls</li>
    <li>Encryption where appropriate</li>
    <li>Secure authentication</li>
    <li>Network monitoring</li>
    <li>Segregation of trust and operating funds</li>
    <li>Regular security reviews</li>
  </ul>

  <h2>7. Data Retention Practices</h2>
  <p>
    Personal information is retained as long as necessary to fulfill the
    purposes outlined in this policy and to comply with legal requirements.
  </p>

  <h2>8. California Privacy Rights</h2>
  <p>
    California residents may have additional rights under applicable law,
    subject to exemptions for financial institutions.
  </p>

  <h2>9. Cookies and Tracking Technologies</h2>
  <p>
    We may use cookies and similar technologies to enhance functionality and
    analyze usage. Disabling cookies may affect site performance.
  </p>

  <h2>10. Communications and Electronic Records</h2>
  <p>
    By using our Services, you consent to receive electronic communications and
    acknowledge that electronic records and signatures are legally binding.
  </p>

  <h2>11. Children’s Privacy</h2>
  <p>
    Our Services are not directed to individuals under the age of 18.
  </p>

  <h2>12. Changes to This Privacy Policy</h2>
  <p>
    We may update this Privacy Policy periodically. Continued use of the
    Services constitutes acceptance of the revised policy.
  </p>

  <h2>13. Contact Information</h2>
  <address>
    <strong>iFlyFinance Inc.</strong><br />
    1881 California Ave, Suite 101<br />
    Corona, CA 92881<br />
    Email: <a href="mailto:support@iflyfinance.com">support&#64;iflyfinance.com</a><br />
    Phone: <a href="tel:18006664359">800.666.4359</a>
  </address>

  <p class="important-notice">
    <strong>Important Notice:</strong>
    This Privacy Policy applies solely to iFlyFinance Inc. and does not apply
    to BWI Aviation Insurance or any other affiliated entities.
  </p>

</section>
